import { useEffect, useState } from "react";
import ClientLayout from "../components/ClientLayout";
import { FileText, Eye, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";

export default function ClientSOA() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [soas, setSOAs] = useState([]);

  useEffect(() => {
    fetchSOAs();
  }, []);

  const fetchSOAs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/soa`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSOAs(response.data.soas || response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/client/login");
        return;
      }
      toast.error("Failed to fetch SOA history");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
      unpaid: "bg-red-100 text-red-700 border-red-200",
      partial: "bg-amber-100 text-amber-700 border-amber-200",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full capitalize ${colors[status] || colors.unpaid}`}
      >
        {status}
      </span>
    );
  };

  // Date formatting helper - MMM-dd-yyyy format (e.g., Jan-01-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // If already in MMM-dd-yyyy format, return as is
      if (/^[A-Z][a-z]{2}-\d{2}-\d{4}/.test(dateStr))
        return dateStr.split(" ")[0];
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
            Statement of Account
          </h1>
          <p className="text-slate-600 mt-1">
            View your billing history and statements
          </p>
          {/* <apre>{JSON.stringify(soas)}</apre> */}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {soas.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No statements available yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {soas.map((soa) => (
                <div
                  key={soa.id}
                  className="p-5 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {soa.soa_number}
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDate(soa.billing_period_start)} -{" "}
                          {formatDate(soa.billing_period_end)}
                        </p>
                        <p className="text-sm text-slate-500">
                          Due: {formatDate(soa.due_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 ml-14 md:ml-0">
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">
                          ₱{soa.total_amount_due.toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/client/soa/${soa.id}`)}
                        className="flex-shrink-0"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}
